import { groupBy, mapValues } from 'lodash';

import {
  UDRsPolyAsset,
  UDRsPolyAssetGroup,
  UDRsPolyAssetStatus,
} from '@common-modules/mediaDetails/models';
import { ElementOf, optimize } from '@utils';

import { GetTrackingEngineResultsResponse } from '../../../models';
import { Adapter } from '../interfaces';

/**
 * Convert engine results and merge with existing saved asset data.
 */
export const udrsEngineV2Adapter = (): Adapter<
  UDRsEngineV2AdapterInbound,
  UDRsPolyAsset
> => ({ isMatch, adapt });

export interface UDRsEngineV2AdapterInbound {
  engine: GetTrackingEngineResultsResponse;
  asset: UDRsPolyAsset;
}

/**
 * Converts engine `series` to UDRAsset `series`.
 */
const mapper =
  (groupId: string) =>
  (
    s: GetTrackingEngineResultsResponse['engineResults']['records'][number]['jsondata']['series'][number]
  ): ElementOf<UDRsPolyAssetGroup['series']> => ({
    id: `${groupId}_${s.startTimeMs}`,
    startTimeMs: s.startTimeMs,
    stopTimeMs: s.stopTimeMs,
    object: {
      id: `${groupId}_${s.startTimeMs}`,
      overlayObjectType: 'spray_paint_udr' as const,
      boundingPoly: s.object.boundingPoly,
    },
  });

// eslint-disable-next-line lodash/prefer-constant
const isMatch = (_: unknown): _ is UDRsEngineV2AdapterInbound => true;

const adapt = ({ engine, asset }: UDRsEngineV2AdapterInbound): UDRsPolyAsset =>
  engine.engineResults.records[0]
    ? {
        boundingPolys: mapValues(
          groupBy(
            engine.engineResults.records[0].jsondata?.series,
            (s) => s?.object?.vendor?.parentUdrId
          ),
          (t, k) => {
            const firstSeries = t[0];
            if (!firstSeries) {
              return null;
            }
            const groupId = firstSeries.object.vendor.parentUdrId;
            const udrGroup = asset.boundingPolys[groupId];
            if (!udrGroup) {
              return null;
            }
            const series = optimize(t.map(mapper(k)));

            // if UDR group has existing udrs they will get merged with the engine results in onActionFetchMoreResults function of the tracking.ts reducer
            return {
              userLabel: udrGroup.userLabel,
              groupId,
              clusterId: udrGroup.clusterId,
              status: UDRsPolyAssetStatus.GROUP,
              basePoly: udrGroup.basePoly,
              series,
              shapeType: udrGroup.shapeType,
              redactionCode: udrGroup.redactionCode,
              textOverlay: udrGroup.textOverlay,
              redactionConfig: udrGroup.redactionConfig,
            };
          }
        ),
      }
    : asset;
